import { tryCatch } from "@/lib/try-catch";
import { Ok, Err } from "ts-results";
import { API_BASE_URL, PAPER_SIZES, TIMEZONES } from "@/lib/constants";
import { Response } from "./types";
import { z } from "zod";
import { ExcelDataSchema } from "@/lib/schemas/noise-dose";

export const generateNoiseDoseReportSchema = z.object({
  criterionLevel: z.coerce.number(),
  exchangeRate: z.coerce.number(),
  actionLevel: z.coerce.number(),
  thresholdLevel: z.coerce.number(),
  uncertaintyRounding: z.coerce.number(),
  decibelRounding: z.coerce.number(),
  doseRounding: z.coerce.number(),
  primaryColor: z.string(),
  headingColor: z.string(),
  goodColor: z.string(),
  warningColor: z.string(),
  badColor: z.string(),
  paperSize: z.enum(
    PAPER_SIZES.map((paperSize) => paperSize.value) as [string, ...string[]]
  ),
  marginInches: z.coerce.number(),
  timezone: z.enum(
    TIMEZONES.map((timezone) => timezone.value) as [string, ...string[]]
  ),
  password: z.string().optional(),
  reportTier: z.enum(["free", "paid"]),
  excelData: ExcelDataSchema,
  inputFilename: z.string(),
  tmpFileKey: z.string(),
  optionalId: z.string().optional(),
  coverImage: z.boolean(),
  outline: z.boolean(),
  sectionDividers: z.boolean(),
});

export type GenerateNoiseDoseReportRequest = z.infer<
  typeof generateNoiseDoseReportSchema
>;

export async function generateNoiseDoseReport(
  request: GenerateNoiseDoseReportRequest
): Promise<Response<string>> {
  const responseResult = await tryCatch(
    fetch(`${API_BASE_URL}/api/v1/reportes/ruido-dosis`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    })
  );

  if (responseResult.err) {
    return Err({
      message: `Error al consultar el servidor: ${responseResult.val.message}`,
      devMessage: responseResult.val.message,
    });
  }

  const response = responseResult.val;

  if (!response.ok) {
    const errMessageResult = await tryCatch(response.json());
    return errMessageResult.err
      ? Err({
          message: `Error al leer la respuesta del servidor: ${errMessageResult.val.message}`,
          devMessage: errMessageResult.val.message,
        })
      : Err(errMessageResult.val);
  }

  const dataResult = await tryCatch(response.text());

  if (dataResult.err) {
    return Err({
      message: `Error al leer la respuesta del servidor: ${dataResult.val.message}`,
      devMessage: dataResult.val.message,
    });
  }

  return Ok(dataResult.val);
}
